import type {
	ArticleThumbnail,
	Author,
	AuthorThumbnail,
	Category,
	Article as FullArticle,
	Image,
	Property
} from '$lib/satellite.types';
import type {
	Article,
	CollectionPage,
	ImageObject,
	ItemList,
	Organization,
	Person,
	SearchAction,
	SearchResultsPage,
	Thing,
	WebPage,
	WebSite,
	WithContext
} from 'schema-dts';
import { getBiggestImage, getGravatarUrl } from './ArticleHelper';
export type Schema = Thing | WithContext<Thing>;

export const serializeSchema = (thing: Schema | Schema[]) => {
	return `<script type="application/ld+json">${JSON.stringify(thing, null)}</script>`;
};

export const organizationSchema = (property: Property): Organization => {
	return {
		'@type': 'Organization',
		name: property.name,
		logo: rawImageSchema(`https://${property.hostname}/logo.png`, 600, 60),
		url: `https://${property.hostname}`
	} as Organization;
};

export const rawImageSchema = (url: string, width: number, height: number): ImageObject => {
	return {
		'@type': 'ImageObject',
		url,
		width: width.toString(),
		height: height.toString()
	} as ImageObject;
};

export const searchSchema = (
	property: Property,
	articles: ArticleThumbnail[],
	title: string,
	description: string,
	url: string
): Schema => {
	const webSite = websiteSchema(property);

	const searchResultsPage = {
		'@type': 'SearchResultsPage',
		name: title,
		description: description,
		url: url
	} as SearchResultsPage;

	searchResultsPage.mainEntity = {
		'@type': 'ItemList',
		itemListElement: articles.map((article: ArticleThumbnail, index: number) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: articleSchema(article, property)
			};
		})
	} as ItemList;

	webSite.hasPart = searchResultsPage;

	return webSite as Schema;
};

export const websiteSchema = (property: Property): WebSite => {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		url: `https://${property.hostname}`,
		name: property.name,
		description: property.description,
		inLanguage: property.language,
		keywords: property.keywords,
		publisher: organizationSchema(property),
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: `https://${property.hostname}/${property.language == 'fr' ? 'recherche' : 'search'}?q={search_term_string}`
			},
			'query-input': 'required name=search_term_string'
		} as SearchAction
	} as WebSite;
};

const imageSchema = (image: Image[], preset: string = '16by9'): ImageObject => {
	const finalImage: Image = getBiggestImage(image, preset);

	if (finalImage) {
		return {
			'@type': 'ImageObject',
			url: finalImage.url,
			width: finalImage.size[0].toString(),
			height: finalImage.size[1].toString()
		} as ImageObject;
	}

	return {} as ImageObject;
};

export const writerPage = (
	author: Author,
	property: Property,
	articles: ArticleThumbnail[],
	title: string,
	description: string,
	url: string
): Schema => {
	const webSite = websiteSchema(property);

	const webPage = {
		'@type': 'WebPage',
		name: title,
		description: description,
		url: url
	} as WebPage;

	webPage.mainEntity = authorSchema(author, property, false, true) as Person;
	webPage.hasPart = {
		'@type': 'WebPageElement',
		mainEntity: {
			'@type': 'ItemList',
			itemListElement: articles.map((article: ArticleThumbnail, index: number) => {
				return {
					'@type': 'ListItem',
					position: index + 1,
					item: articleSchema(article, property, false)
				};
			})
		} as ItemList
	};

	webSite.hasPart = webPage;

	return webSite as Schema;
};

export const writersPage = (
	authors: AuthorThumbnail[],
	property: Property,
	title: string,
	description: string,
	url: string
): Schema => {
	const webSite = websiteSchema(property);

	const collectionPage = {
		'@type': 'CollectionPage',
		name: title,
		description: description,
		url
	} as CollectionPage;

	collectionPage.mainEntity = {
		'@type': 'ItemList',
		itemListElement: authors.map((author: AuthorThumbnail, index: number) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: authorSchema(author, property) as Person
			};
		})
	} as ItemList;

	webSite.hasPart = collectionPage;

	return webSite as Schema;
};

export const informationPage = (
	property: Property,
	title: string,
	description: string,
	url: string
): Schema => {
	const webSite = websiteSchema(property);

	const webPage = {
		'@type': 'WebPage',
		name: title,
		description,
		url
	} as WebPage;

	webSite.hasPart = webPage;

	return webSite as Schema;
};

export const articleSchema = (
	article: FullArticle | ArticleThumbnail,
	property: Property,
	full: boolean = false
): Article => {
	const schema = {
		'@type': 'Article',
		headline: article.title,
		name: article.title,
		url: `https://${property.hostname}/${article.category.slug}/${article.slug}`,
		author: authorSchema(article.author, property, article.anonymous),
		image: imageSchema(article.imageFeatured),
		articleSection: article.category.name,
		inLanguage: property.language,
		datePublished: article.published.toISOString()
	} as Article;

	if (article.modified) {
		schema.dateModified = article.modified.toISOString();
	}

	if (full) {
		schema.publisher = organizationSchema(property);

		if ('keywords' in article && article.keywords) {
			schema.keywords = article.keywords.join(', ');
		}

		if ('description' in article && article.description) {
			schema.description = article.description;
		}
	}

	return schema as Article;
};

export const fullArticleSchema = (article: FullArticle, property: Property): Schema => {
	const webSite = websiteSchema(property);

	const webPage = {
		'@type': 'WebPage',
		name: article.title,
		description: article.description,
		url: `https://${property.hostname}/${article.category.slug}/${article.slug}`
	} as WebPage;

	webPage.mainEntity = articleSchema(article, property, true);

	webSite.hasPart = webPage;

	return webSite as Schema;
};

export const authorSchema = (
	author: Author | AuthorThumbnail,
	property: Property,
	anonymous: boolean = false,
	full: boolean = false
): Person | Organization => {
	if (anonymous) {
		return organizationSchema(property);
	}

	const schema = {
		'@type': 'Person',
		name: author.name,
		jobTitle: author.title,
		url: `https://${property.hostname}/informations/${property.language == 'fr' ? 'nos-redacteurs' : 'our-writers'}/${author.slug}`,
		image: rawImageSchema(getGravatarUrl(author.gravatarId, 80), 80, 80),
		description: full && 'bio' in author ? author.bio : undefined,
		worksFor: organizationSchema(property)
	} as Person;

	return schema;
};

export const categorySchema = (
	category: Category,
	articles: ArticleThumbnail[],
	property: Property,
	title: string,
	description: string,
	after: string | null = null
): Schema => {
	const webSite = websiteSchema(property);

	const collectionPage = {
		'@type': 'CollectionPage',
		name: title,
		description: description,
		url: `https://${property.hostname}/${category.slug}${after ? `/?after=${after}` : ''}`
	} as CollectionPage;

	collectionPage.mainEntity = {
		'@type': 'ItemList',
		itemListElement: articles.map((article: ArticleThumbnail, index: number) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: articleSchema(article, property)
			};
		})
	} as ItemList;

	webSite.hasPart = collectionPage;

	return webSite as Schema;
};

export const homepageSchema = (
	articles: ArticleThumbnail[],
	property: Property,
	title: string,
	description: string,
	after: string | null = null
): Schema => {
	const webSite = websiteSchema(property);

	const collectionPage = {
		'@type': 'CollectionPage',
		name: title,
		description: description,
		url: `https://${property.hostname}${after ? `/?after=${after}` : ''}`
	} as CollectionPage;

	collectionPage.mainEntity = {
		'@type': 'ItemList',
		itemListElement: articles.map((article: ArticleThumbnail, index: number) => {
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: articleSchema(article, property)
			};
		})
	} as ItemList;

	webSite.hasPart = collectionPage;

	return webSite as Schema;
};
